import type { RefObject } from "react";
import type { DataGridHandle } from "@design-system/DataGrid";
import { hasOpenMenu } from "@design-system/Menu/hasOpenMenu";
import { hasOpenPopover } from "@design-system/Menu/hasOpenPopover";
import { useHasOpenModal } from "@design-system/overlay/openModalLock";
import { useKeyboardShortcutOwner } from "@design-system/ViewTable/core/useKeyboardShortcutOwner";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";

type RowStep = "next" | "previous";

/** Arrows inside a grid belong to that grid's own keys. */
const isInsideGrid = ({ target }: KeyboardEvent) =>
    target instanceof Element &&
    target.closest("[role='grid'], [role='treegrid']") !== null;

/**
 * The Work list's document-wide keys: j/k and Mod+ArrowDown/ArrowUp move row
 * focus, x toggles the focused row, Shift+A toggles every work row. With
 * focus outside the grid, ArrowDown/ArrowUp move row focus too, and
 * Shift+ArrowDown/ArrowUp start a run with the focused row and the next one;
 * inside it, the grid's own arrows apply. They work with focus anywhere on the page, but not
 * in inputs or while a menu, popover, or modal is open. The grid leaves the
 * other keys alone, so each press runs once. Call `activate` when the pointer
 * or focus enters the list, so it owns them.
 */
export const useWorkListShortcuts = ({
    gridRef,
    onToggleListRow,
    onSelectListRow,
    onToggleAllListRows,
}: {
    gridRef: RefObject<DataGridHandle | null>;
    onToggleListRow: (rowId: string) => void;
    onSelectListRow: (rowId: string) => void;
    onToggleAllListRows: () => void;
}) => {
    const {
        isKeyboardShortcutsEnabledForTable,
        activateKeyboardShortcutsForTable,
    } = useKeyboardShortcutOwner({
        tableId: "clients-work-list",
        enableKeyboardShortcuts: true,
    });
    const hasOpenModal = useHasOpenModal();
    const isOverlayOpen = () =>
        hasOpenMenu() || hasOpenPopover() || hasOpenModal;
    const toOptions = (description: string) => ({
        description,
        enabled: isKeyboardShortcutsEnabledForTable,
        ignoreEventWhen: isOverlayOpen,
        preventDefault: false,
    });
    const toOutsideGridOptions = (description: string) => ({
        ...toOptions(description),
        ignoreEventWhen: (event: KeyboardEvent) =>
            isInsideGrid(event) || isOverlayOpen(),
        preventDefault: true,
    });
    const runOnFocusedRow = (handler: (rowId: string) => void) => {
        const rowId = gridRef.current?.getFocusedRowId();
        if (rowId) {
            handler(rowId);
        }
    };
    const selectFocusedRow = () => runOnFocusedRow(onSelectListRow);
    /** Starts a run as the grid's own Shift+Arrow does: the row it lands on is selected too. */
    const selectAndStep = (step: RowStep) => {
        selectFocusedRow();
        gridRef.current?.focusRow(step);
        selectFocusedRow();
    };

    useKeyboardCommand({
        keys: "j",
        callback: () => gridRef.current?.focusRow("next"),
        options: toOptions("Go to next row"),
    });
    useKeyboardCommand({
        keys: "k",
        callback: () => gridRef.current?.focusRow("previous"),
        options: toOptions("Go to previous row"),
    });
    useKeyboardCommand({
        keys: "ArrowDown",
        callback: () => gridRef.current?.focusRow("next"),
        options: toOutsideGridOptions("Go to next row"),
    });
    useKeyboardCommand({
        keys: "ArrowUp",
        callback: () => gridRef.current?.focusRow("previous"),
        options: toOutsideGridOptions("Go to previous row"),
    });
    useKeyboardCommand({
        keys: "shift+ArrowDown",
        callback: () => selectAndStep("next"),
        options: toOutsideGridOptions(
            "Select current row and go to the next row",
        ),
    });
    useKeyboardCommand({
        keys: "shift+ArrowUp",
        callback: () => selectAndStep("previous"),
        options: toOutsideGridOptions(
            "Select current row and go to the previous row",
        ),
    });
    useKeyboardCommand({
        keys: "mod+ArrowDown",
        callback: () => gridRef.current?.focusRow("last"),
        options: { ...toOptions("Jump to the last row"), preventDefault: true },
    });
    useKeyboardCommand({
        keys: "mod+ArrowUp",
        callback: () => gridRef.current?.focusRow("first"),
        options: {
            ...toOptions("Jump to the first row"),
            preventDefault: true,
        },
    });
    useKeyboardCommand({
        keys: "x",
        callback: () => runOnFocusedRow(onToggleListRow),
        options: toOptions("Select or deselect row"),
    });
    useKeyboardCommand({
        keys: "shift+a",
        callback: onToggleAllListRows,
        options: toOptions("Select or deselect all rows"),
    });

    return { activate: activateKeyboardShortcutsForTable };
};
